/* eslint-disable max-lines */
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { css, StyleSheet } from 'aphrodite';
import {
  Control,
  Controller,
  FieldErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';
import SelectInput from '@components/atoms/ui-kit/select-input.tsx';
import Modal from '@components/atoms/modals/modal.tsx';
import {
  UpdateWebhookPayload,
  useUpdateWebhook
} from '@utils/api/back-office/webhooks/use-update-webhook.tsx';
import { useEffect, useRef, useState, useMemo } from 'react';
import { useGetWebhook } from '@utils/api/back-office/webhooks/use-get-webhook.tsx';
import { CreateWebhookPayload } from '@utils/api/back-office/webhooks/use-create-webhook.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base';
import toast from 'react-hot-toast';

type Props = {
  customerId: string | undefined;
  selectedWebhook: number;
  setValue: UseFormSetValue<CreateWebhookPayload>;
  editModalOpen: boolean;
  setIsEditModalOpen: (value: boolean) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleSubmit: UseFormHandleSubmit<any>;
  register: UseFormRegister<CreateWebhookPayload>;
  errors: FieldErrors<CreateWebhookPayload>;
  control: Control<CreateWebhookPayload>;
  reset: UseFormReset<CreateWebhookPayload>;
  watch: UseFormWatch<CreateWebhookPayload>;
};

export default function UpdateWebhookForm({
  customerId,
  selectedWebhook,
  setValue,
  editModalOpen,
  setIsEditModalOpen,
  handleSubmit,
  errors,
  control,
  register,
  reset,
  watch
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const formRef = useRef<HTMLFormElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: webhookData,
    isLoading,
    isError
  } = useGetWebhook(Number(customerId), selectedWebhook);

  const { mutate: updateWebhook } = useUpdateWebhook(
    Number(customerId),
    selectedWebhook
  );

  const headersValue = watch('webhook_headers');

  const handleUpdateWebhook = (data: UpdateWebhookPayload) => {
    const payload: UpdateWebhookPayload = {
      ...data,
      organization_id: Number(customerId),
      webhook_headers: headersValue
    };

    setIsSubmitting(true);

    updateWebhook(payload, {
      onSuccess: () => {
        toast.success(t('webhook-updated-successfully'));
        setIsEditModalOpen(false);
        setIsSubmitting(false);
      },
      onError: (error: AxiosError<BaseError>) => {
        const data = error.response?.data;
        const detail = data?.detail;
        const detailMessage = Array.isArray(detail)
          ? detail
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((d: any) => d?.msg || d?.message)
              .filter(Boolean)
              .join('; ')
          : typeof detail === 'string'
            ? detail
            : undefined;

        const errorMessage =
          data?.error_description ||
          detailMessage ||
          data?.error ||
          data?.message ||
          t('an-error-occurred-while-updating-the-webhook');

        toast.error(errorMessage);
        setIsSubmitting(false);
      }
    });
  };

  const webhookMethodOptions = [
    { value: 'POST', label: 'POST' },
    { value: 'PUT', label: 'PUT' }
  ];

  const webhookTypeOptions = [
    { value: 'REVIEWER_CALLBACK', label: 'REVIEWER_CALLBACK' },
    { value: 'ADMIN_CALLBACK', label: 'ADMIN_CALLBACK' },
    {
      value: 'ADMIN_FROM_SESSION_CALLBACK',
      label: 'ADMIN_FROM_SESSION_CALLBACK'
    },
    {
      value: 'EXCEEDANCE_NOTIFICATION',
      label: 'EXCEEDANCE_NOTIFICATION'
    }
  ];

  useEffect(() => {
    if (editModalOpen && selectedWebhook && webhookData) {
      setValue('type', webhookData.result.type);
      setValue('webhook_url', webhookData.result.webhook_url);
      setValue('webhook_method', webhookData.result.webhook_method);
      setValue('webhook_headers', webhookData.result.webhook_headers || []);
    }
  }, [editModalOpen, selectedWebhook, webhookData, setValue]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        card: {
          backgroundColor: getColour('appSoft'),
          border: `1px solid ${getColour('hairline')}`,
          borderRadius: 16
        },
        cardHeader: {
          borderBottom: `1px solid ${getColour('hairline')}`
        },
        subtle: {
          color: getColour('icon')
        },
        label: {
          color: getColour('normalText')
        },
        headerRow: {
          backgroundColor: getColour('thirdLayer'),
          borderColor: getColour('hairline')
        }
      }),
    [getColour]
  );

  return (
    <Modal
      isOpen={editModalOpen}
      onClose={() => {
        reset(
          {
            type: '',
            webhook_url: '',
            webhook_method: '',
            webhook_headers: []
          },
          { keepValues: false }
        );
        setIsEditModalOpen(false);
      }}
      size="medium"
      title={t('update-webhook')}
      caption={
        t('webhook-update-description') ||
        'Modify the webhook configuration. Changes will take effect immediately.'
      }
      footerActions={[
        <Button
          key="cancel"
          onClick={() => {
            reset(
              {
                type: '',
                webhook_url: '',
                webhook_method: '',
                webhook_headers: []
              },
              { keepValues: false }
            );
            setIsEditModalOpen(false);
          }}
          label={t('cancel')}
          size="small"
          variant="smoke"
          disabled={isSubmitting}
        />,
        <Button
          key="update"
          type="submit"
          onClick={() => formRef.current?.requestSubmit()}
          label={isSubmitting ? `${t('updating')}...` : t('update')}
          size="small"
          variant="primary"
          disabled={isSubmitting}
        />
      ]}
    >
      {isLoading ? (
        <div className="flex text-center w-full justify-center pt-2">
          Loading...
        </div>
      ) : isError ? (
        <div className="pt-2 text-sm text-red-500 text-center">
          {t('an-error-occurred-while-fetching-the-webhook')}
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(handleUpdateWebhook)}
          ref={formRef}
          className="space-y-5"
        >
          {/* CONFIG CARD */}
          <div className={cn(css(styles.card), 'overflow-hidden')}>
            <div className={cn(css(styles.cardHeader), 'px-4 py-3')}>
              <p className={cn(css(styles.label), 'text-sm font-semibold')}>
                {t('webhook-configuration') || 'Webhook configuration'}
              </p>
              <p className={cn(css(styles.subtle), 'text-xs mt-0.5')}>
                {t('webhook-update-description') ||
                  'Modify the webhook configuration. Changes will take effect immediately.'}
              </p>
            </div>

            <div className="p-4 md:p-5 space-y-4">
              {/* Type */}
              <div className="space-y-1.5">
                <label
                  htmlFor="type"
                  className={cn(css(styles.label), 'block text-sm font-medium')}
                >
                  {t('webhook-type')} <span className="text-red-500">*</span>
                </label>

                <Controller
                  control={control}
                  name="type"
                  defaultValue={webhookTypeOptions[0].value}
                  render={({ field: { onChange, value } }) => (
                    <SelectInput
                      id="type"
                      name="type"
                      value={
                        webhookTypeOptions.find(
                          option => option.value === value
                        ) || null
                      }
                      onChange={(selectedOption: {
                        value: string;
                        label: string;
                      }) => {
                        onChange(selectedOption?.value || '');
                      }}
                      options={webhookTypeOptions}
                      isMulti={false}
                      error={errors.type && errors.type.message}
                    />
                  )}
                />

                <p className={cn(css(styles.subtle), 'text-xs')}>
                  {t('webhook-type-hint') ||
                    'Select when this webhook should be triggered'}
                </p>
              </div>

              {/* URL */}
              <div className="space-y-1.5">
                <Input
                  id="webhook_url"
                  label={
                    <>
                      {t('webhook-url')} <span className="text-red-500">*</span>
                    </>
                  }
                  type="text"
                  defaultValue={webhookData?.result.webhook_url}
                  placeholder="https://your-domain.com/webhook"
                  {...register('webhook_url')}
                  error={errors.webhook_url?.message}
                />
                <p className={cn(css(styles.subtle), 'text-xs')}>
                  {t('webhook-url-hint') ||
                    'The endpoint URL where webhook data will be sent'}
                </p>
              </div>

              {/* Method */}
              <div className="space-y-1.5">
                <label
                  htmlFor="webhook_method"
                  className={cn(css(styles.label), 'block text-sm font-medium')}
                >
                  {t('http-method')} <span className="text-red-500">*</span>
                </label>

                <Controller
                  control={control}
                  name="webhook_method"
                  render={({ field: { onChange, value } }) => (
                    <SelectInput
                      id="webhook_method"
                      name="webhook_method"
                      value={
                        webhookMethodOptions.find(
                          option => option.value === value
                        ) || null
                      }
                      onChange={(selectedOption: {
                        value: string;
                        label: string;
                      }) => {
                        onChange(selectedOption ? selectedOption.value : null);
                      }}
                      options={webhookMethodOptions}
                      isMulti={false}
                      error={
                        errors.webhook_method && errors.webhook_method.message
                      }
                    />
                  )}
                />

                <p className={cn(css(styles.subtle), 'text-xs')}>
                  {t('http-method-hint') ||
                    'The HTTP method to use when calling the webhook'}
                </p>
              </div>
            </div>
          </div>

          {/* HEADERS CARD */}
          <div className={cn(css(styles.card), 'overflow-hidden')}>
            <div className={cn(css(styles.cardHeader), 'px-4 py-3')}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={cn(css(styles.label), 'text-sm font-semibold')}>
                    {t('custom-headers')}{' '}
                    {t('optional') ? (
                      <span className={cn(css(styles.subtle), 'font-normal')}>
                        ({t('optional')})
                      </span>
                    ) : null}
                  </p>
                  <p className={cn(css(styles.subtle), 'text-xs mt-0.5')}>
                    {t('webhook-headers-hint') ||
                      'Add custom HTTP headers to include with each webhook request. Format: Header-Name: Header-Value'}
                  </p>
                </div>

                <span
                  className={cn(
                    css(styles.subtle),
                    'text-[11px] px-2 py-1 rounded-full border whitespace-nowrap'
                  )}
                  style={{ borderColor: getColour('hairline') }}
                >
                  {t('advanced') || 'Advanced'}
                </span>
              </div>
            </div>

            <div className="p-4 md:p-5">
              <Controller
                name="webhook_headers"
                control={control}
                render={({ field: { onChange, value } }) => {
                  const headers: string[] = Array.isArray(value) ? value : [];

                  const handleAddHeader = () => {
                    onChange([...headers, '']);
                  };

                  const handleHeaderChange = (
                    index: number,
                    newVal: string
                  ) => {
                    const updated = [...headers];
                    updated[index] = newVal;
                    onChange(updated);
                  };

                  const handleRemoveHeader = (index: number) => {
                    const updated = [...headers];
                    updated.splice(index, 1);
                    onChange(updated);
                  };

                  return (
                    <div className="space-y-3">
                      {headers.length === 0 ? (
                        <div
                          className={cn(
                            'text-sm italic py-3 px-4 rounded-xl border',
                            css(styles.subtle)
                          )}
                          style={{ borderColor: getColour('hairline') }}
                        >
                          {t('no-headers-added') ||
                            'No custom headers configured'}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {headers.map((header, idx) => (
                            <div
                              key={idx}
                              className={cn(
                                'flex items-center gap-2 rounded-xl border p-3',
                                css(styles.headerRow)
                              )}
                              style={{ borderColor: getColour('hairline') }}
                            >
                              <div className="flex-1 min-w-0">
                                <input
                                  type="text"
                                  value={header}
                                  placeholder="Authorization: Bearer your-token-here"
                                  onChange={e =>
                                    handleHeaderChange(idx, e.target.value)
                                  }
                                  className={cn(
                                    'w-full px-3 py-2 rounded-2xl border text-sm focus:outline-none',
                                    'focus:ring-2 focus:ring-primary',
                                    'shadow-sm focus:shadow',
                                    'min-h-[40px]'
                                  )}
                                  style={{
                                    backgroundColor: getColour('appSoft'),
                                    borderColor: getColour('hairline'),
                                    color: getColour('normalText')
                                  }}
                                />
                              </div>

                              <button
                                type="button"
                                onClick={() => handleRemoveHeader(idx)}
                                className="text-red-500 hover:text-red-700 text-sm font-medium px-2 py-2 rounded-2xl"
                              >
                                {t('remove')}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleAddHeader}
                        className="text-sm font-medium hover:opacity-80"
                        style={{ color: getColour('activeText') }}
                      >
                        + {t('add-header')}
                      </button>

                      {errors.webhook_headers && (
                        <p className="text-red-500 text-sm mt-2">
                          {errors.webhook_headers.message}
                        </p>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </form>
      )}
    </Modal>
  );
}
